<template>
  <div class="contact-form-card">
    <div v-if="showHeader" class="form-header">
      <h3 class="form-title">{{ title }}</h3>
      <p v-if="subtitle" class="form-subtitle">{{ subtitle }}</p>
    </div>
    <form @submit.prevent="handleContactSubmit" class="contact-form">
      <div class="form-row">
        <div class="form-group">
          <label :for="`${formId}-firstName`" class="form-label">
            <i class="fas fa-user form-icon"></i>
            Nombre de pila <span class="required-asterisk">*</span>
          </label>
          <div class="input-wrapper">
            <input
              type="text"
              :id="`${formId}-firstName`"
              v-model="contactForm.firstName"
              class="form-control"
              placeholder="Ingresa tu nombre"
              required
              @focus="handleInputFocus"
              @blur="handleInputBlur"
            />
            <span class="input-focus-line"></span>
          </div>
        </div>

        <div class="form-group">
          <label :for="`${formId}-lastName`" class="form-label">
            <i class="fas fa-user form-icon"></i>
            Apellido <span class="required-asterisk">*</span>
          </label>
          <div class="input-wrapper">
            <input
              type="text"
              :id="`${formId}-lastName`"
              v-model="contactForm.lastName"
              class="form-control"
              placeholder="Ingresa tu apellido"
              required
              @focus="handleInputFocus"
              @blur="handleInputBlur"
            />
            <span class="input-focus-line"></span>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label :for="`${formId}-email`" class="form-label">
          <i class="fas fa-envelope form-icon"></i>
          Email <span class="required-asterisk">*</span>
        </label>
        <div class="input-wrapper">
          <input
            type="email"
            :id="`${formId}-email`"
            v-model="contactForm.email"
            class="form-control"
            placeholder="tu@email.com"
            required
            @focus="handleInputFocus"
            @blur="handleInputBlur"
          />
          <span class="input-focus-line"></span>
        </div>
      </div>

      <div class="form-group">
        <label :for="`${formId}-phone`" class="form-label">
          <i class="fas fa-phone form-icon"></i>
          Teléfono <span class="required-asterisk">*</span>
        </label>
        <div class="input-wrapper">
          <input
            type="tel"
            :id="`${formId}-phone`"
            v-model="contactForm.phone"
            class="form-control"
            placeholder="(123) 456-7890"
            required
            @focus="handleInputFocus"
            @blur="handleInputBlur"
          />
          <span class="input-focus-line"></span>
        </div>
      </div>

      <div class="form-group">
        <label :for="`${formId}-planInterest`" class="form-label">
          <i class="fas fa-clipboard-list form-icon"></i>
          Plan de interés <span class="required-asterisk">*</span>
        </label>
        <div class="select-wrapper custom-select-display">
          <select
            :id="`${formId}-planInterest`"
            v-model="contactForm.planInterest"
            class="form-control select-native-overlay"
            required
            @focus="handleInputFocus"
            @blur="handleInputBlur"
          >
            <option value="" disabled>Selecciona un plan</option>
            <option
              v-for="plan in plans"
              :key="`plan-${plan.id || plan.memberships_parameters_id}`"
              :value="String(plan.id || plan.memberships_parameters_id || '')"
            >
              {{ plan.name || `Plan ${plan.id}` }}
            </option>
          </select>
          <span class="select-display-text" :class="{ 'is-placeholder': !contactForm.planInterest }">{{ selectedPlanLabel || 'Selecciona un plan' }}</span>
          <i class="fas fa-chevron-down select-arrow-icon"></i>
          <span class="input-focus-line"></span>
        </div>
        <small v-if="plans.length === 0 && !loading" class="form-help-text">
          No hay planes disponibles
        </small>
        <small v-else-if="loading" class="form-help-text">
          Cargando planes...
        </small>
      </div>

      <div v-if="requiresDateBirth || contactForm.date_birth" class="form-group">
        <label :for="`${formId}-dateBirth`" class="form-label">
          <i class="fas fa-calendar form-icon"></i>
          Fecha de nacimiento
          <span v-if="requiresDateBirth" class="required-asterisk">*</span>
        </label>
        <div class="input-wrapper">
          <input
            type="text"
            :id="`${formId}-dateBirth`"
            :value="contactForm.date_birth"
            class="form-control"
            placeholder="MM-DD-YYYY"
            maxlength="10"
            inputmode="numeric"
            autocomplete="bday"
            :required="requiresDateBirth"
            @input="onDateBirthInput"
            @focus="handleInputFocus"
            @blur="handleInputBlur"
          />
          <span class="input-focus-line"></span>
        </div>
        <small v-if="requiresDateBirth" class="form-help-text">
          Obligatorio para Tu Salud Senior (60+). Formato MM-DD-YYYY.
        </small>
      </div>

      <div class="form-group">
        <label :for="`${formId}-preferredLanguage`" class="form-label">
          <i class="fas fa-language form-icon"></i>
          Idioma preferido
        </label>
        <div class="select-wrapper custom-select-display">
          <select
            :id="`${formId}-preferredLanguage`"
            v-model="contactForm.preferredLanguage"
            class="form-control select-native-overlay"
            @focus="handleInputFocus"
            @blur="handleInputBlur"
          >
            <option value="" disabled>Selecciona un idioma</option>
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
          <span class="select-display-text" :class="{ 'is-placeholder': !contactForm.preferredLanguage }">{{ selectedLanguageLabel || 'Selecciona un idioma' }}</span>
          <i class="fas fa-chevron-down select-arrow-icon"></i>
          <span class="input-focus-line"></span>
        </div>
      </div>

      <div class="form-group checkbox-group">
        <div class="custom-checkbox">
          <input
            type="checkbox"
            :id="`${formId}-smsConsent`"
            v-model="contactForm.smsConsent"
            class="form-checkbox"
          />
          <label :for="`${formId}-smsConsent`" class="checkbox-label">
            <span class="checkbox-custom" :class="{ 'is-checked': contactForm.smsConsent }"></span>
            <span class="checkbox-text">
              I Consent to Receive SMS Notifications, Alerts & Occasional Marketing Communication from <strong>Hispano Medical Plan</strong>. Message frequency varies. Message & data rates may apply. Text HELP to <strong>214-888-9000</strong> for assistance. You can reply STOP to unsubscribe at any time.
            </span>
          </label>
        </div>
      </div>

      <button type="submit" class="btn btn-submit-form" :disabled="contactFormSubmitting">
        <span v-if="!contactFormSubmitting">
          <i class="fas fa-paper-plane mr-2"></i>
          Solicitar información
        </span>
        <span v-else class="submit-loading">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          Enviando...
        </span>
      </button>
    </form>
  </div>
</template>

<script>
import plansService from '@/core/services/api/landings/plans'
import ToastificationContent from '@/core/plugins/toastification/ToastificationContent.vue'
import {
  formatRegisterApiMessage,
  isSeniorPlan,
  isValidDateBirthMdy,
  maskDateBirthInput
} from '../utils/registerUserPlanHelpers'

/**
 * Formulario de contacto reutilizable para solicitar información.
 * No inicia compra: los CTAs de planes van a /comprar-plan.
 */
export default {
  name: 'ContactFormCard',
  components: {
    ToastificationContent
  },
  props: {
    /** Prefijo único para los ids de los campos (evita conflictos si hay varios formularios) */
    formId: {
      type: String,
      default: 'contactForm'
    },
    /** Mostrar encabezado con título */
    showHeader: {
      type: Boolean,
      default: false
    },
    /** Título del formulario */
    title: {
      type: String,
      default: '¿Necesitas más información?'
    },
    /** Subtítulo del formulario */
    subtitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      plans: [],
      loading: true,
      contactForm: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        planInterest: '',
        date_birth: '',
        preferredLanguage: '',
        smsConsent: false
      },
      contactFormSubmitting: false
    }
  },
  computed: {
    selectedPlan() {
      if (!this.contactForm.planInterest) return null
      return this.plans.find(p =>
        String(p.id || p.memberships_parameters_id || '') === this.contactForm.planInterest
      ) || null
    },
    selectedPlanLabel() {
      return this.selectedPlan ? (this.selectedPlan.name || `Plan ${this.selectedPlan.id}`) : ''
    },
    requiresDateBirth() {
      return isSeniorPlan(this.selectedPlan)
    },
    selectedLanguageLabel() {
      const map = { es: 'Español', en: 'English' }
      return map[this.contactForm.preferredLanguage] || ''
    }
  },
  mounted() {
    this.loadPlans()
  },
  methods: {
    onDateBirthInput(event) {
      this.contactForm.date_birth = maskDateBirthInput(event.target.value)
    },
    async loadPlans() {
      this.loading = true
      try {
        const response = await plansService.getAllPlans()
        let plansData = []
        if (response && response.status === 'ok' && response.data && Array.isArray(response.data)) {
          plansData = response.data
        } else if (Array.isArray(response)) {
          plansData = response
        } else if (response && response.data && Array.isArray(response.data)) {
          plansData = response.data
        }
        this.plans = plansData
      } catch (error) {
        console.error('Error loading plans:', error)
        this.plans = []
      } finally {
        this.loading = false
      }
    },
    handleInputFocus(event) {
      const wrapper = event.target.closest('.input-wrapper, .select-wrapper')
      if (wrapper) {
        wrapper.classList.add('is-focused')
      }
    },
    handleInputBlur(event) {
      const wrapper = event.target.closest('.input-wrapper, .select-wrapper')
      if (wrapper) {
        wrapper.classList.remove('is-focused')
        if (event.target.value) {
          wrapper.classList.add('has-value')
        } else {
          wrapper.classList.remove('has-value')
        }
      }
    },
    async handleContactSubmit() {
      if (this.requiresDateBirth && !this.contactForm.date_birth) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Fecha de nacimiento',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'La fecha de nacimiento es obligatoria para este plan'
          }
        })
        return
      }
      if (this.contactForm.date_birth && !isValidDateBirthMdy(this.contactForm.date_birth)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Fecha de nacimiento',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'La fecha de nacimiento debe tener el formato MM-DD-YYYY'
          }
        })
        return
      }

      this.contactFormSubmitting = true
      try {
        const selectedPlan = this.selectedPlan
        const membershipValue = selectedPlan ? (Number(selectedPlan.cc) || Number(selectedPlan.price) || 0) : 0

        const formData = {
          name: this.contactForm.firstName,
          lastname: this.contactForm.lastName,
          email: this.contactForm.email,
          phone: this.contactForm.phone,
          rol_id: 10,
          memberships_parameters_id: Number(this.contactForm.planInterest),
          cash: 0,
          cc: membershipValue
        }
        if (this.contactForm.date_birth) {
          formData.date_birth = this.contactForm.date_birth.trim()
        }

        await plansService.registerUserPlan(formData)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: '¡Éxito!',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Tu información ha sido enviada correctamente. Nos pondremos en contacto contigo.'
          }
        })

        this.contactForm = {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          planInterest: '',
          date_birth: '',
          preferredLanguage: '',
          smsConsent: false
        }

        this.$nextTick(() => {
          const wrappers = this.$el.querySelectorAll('.input-wrapper, .select-wrapper')
          wrappers.forEach(wrapper => {
            wrapper.classList.remove('has-value', 'is-focused')
          })
        })

        this.$emit('success')
      } catch (error) {
        console.error('Error submitting contact form:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: formatRegisterApiMessage(error, 'Hubo un error al enviar tu información. Por favor, intenta de nuevo.')
          }
        })
        this.$emit('error', error)
      } finally {
        this.contactFormSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.contact-form-card {
  background: $bg-white;
  border-radius: 25px;
  padding: 4rem;
  box-shadow: 0 15px 50px rgba(45, 106, 110, 0.12);
  border: 3px solid $border-light;

  .form-header {
    margin-bottom: 2rem;

    .form-title {
      font-size: 1.8rem;
      font-weight: 700;
      color: $primary-color;
      margin-bottom: 0.5rem;
    }

    .form-subtitle {
      font-size: 1rem;
      color: $text-medium;
      margin: 0;
    }
  }

  .contact-form {
    .form-row {
      display: flex;
      gap: 2rem;
      margin-bottom: 0;

      .form-group {
        flex: 1;
      }
    }

    .form-group {
      margin-bottom: 2rem;
      position: relative;

      .form-label {
        color: $primary-color;
        font-size: 1.1rem;
        font-weight: 600;
        margin-bottom: 1rem;
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .form-icon {
          font-size: 1rem;
          opacity: 0.9;
        }

        .required-asterisk {
          color: #F64E60;
          font-weight: 700;
          margin-left: 3px;
        }
      }

      /* Misma estructura que input-wrapper: select-wrapper para estilos consistentes */
      .input-wrapper,
      .select-wrapper {
        position: relative;

        .form-control {
          width: 100%;
          padding: 16px 20px;
          border: 3px solid $border-light;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 500;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: $bg-light;
          color: $text-dark;

          &:focus {
            outline: none;
            border-color: $primary-color;
            background: $bg-white;
            box-shadow: 0 0 0 4px rgba(45, 106, 110, 0.1);
          }

          &::placeholder {
            color: #B5B5C3;
            font-size: 1rem;
          }
        }

        .input-focus-line {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 3px;
          pointer-events: none;
          background: $primary-gradient;
          transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 0 0 12px 12px;
        }

        .form-control:focus + .input-focus-line,
        &.is-focused .input-focus-line {
          width: 100%;
        }
      }

      /* Custom select: overlay nativo invisible + span visible (evita bug Safari/WebKit que oculta texto) */
      .select-wrapper.custom-select-display {
        display: flex;
        align-items: center;
        position: relative;
        border: 3px solid $border-light;
        border-radius: 12px;
        background: $bg-light;
        min-height: 52px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &.is-focused {
          border-color: $primary-color;
          background: $bg-white;
          box-shadow: 0 0 0 4px rgba(45, 106, 110, 0.1);
        }

        .select-native-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
          z-index: 2;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;

          option {
            background: $bg-white;
            color: $text-dark;
          }
        }

        .select-display-text {
          flex: 1;
          padding: 16px 45px 16px 20px;
          font-size: 1.1rem;
          font-weight: 500;
          color: $text-dark;
          pointer-events: none;
          min-height: 1.1em;

          &:empty::before,
          &.is-placeholder {
            color: #B5B5C3;
          }
        }

        .select-arrow-icon {
          position: absolute;
          right: 20px;
          font-size: 0.75rem;
          color: $primary-color;
          pointer-events: none;
          z-index: 1;
        }
      }

      .form-help-text {
        display: block;
        margin-top: 0.75rem;
        font-size: 0.95rem;
        color: $text-medium;
        font-style: italic;
      }

      &.checkbox-group {
        margin-bottom: 2rem;

        .custom-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 1.25rem;

          .form-checkbox {
            display: none;
          }

          .checkbox-label {
            display: flex;
            align-items: flex-start;
            gap: 1.25rem;
            cursor: pointer;
            margin: 0;

            .checkbox-custom {
              width: 28px;
              height: 28px;
              border: 3px solid $border-light;
              border-radius: 8px;
              background: $bg-white;
              position: relative;
              transition: all 0.3s ease;
              flex-shrink: 0;
              margin-top: 3px;

              &::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) scale(0);
                width: 14px;
                height: 14px;
                background: white;
                border-radius: 4px;
                transition: transform 0.2s ease;
              }
            }

            .form-checkbox:checked + .checkbox-label .checkbox-custom,
            .checkbox-custom.is-checked {
              border-color: $primary-color;
              background: $primary-color;

              &::after {
                transform: translate(-50%, -50%) scale(1);
              }
            }

            .checkbox-text {
              font-size: 0.95rem;
              color: $text-medium;
              line-height: 1.7;
              flex: 1;
            }
          }
        }
      }
    }

    .btn-submit-form {
      width: 100%;
      background: $primary-gradient;
      color: white;
      border: none;
      border-radius: 15px;
      padding: 22px 45px;
      font-size: 1.25rem;
      font-weight: 700;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 12px 35px rgba(45, 106, 110, 0.4);
      cursor: pointer;
      margin-top: 1rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;

      &:hover:not(:disabled) {
        transform: translateY(-4px);
        box-shadow: 0 18px 45px rgba(45, 106, 110, 0.5);
        background: linear-gradient(135deg, $primary-dark 0%, $primary-color 100%);
      }

      &:active:not(:disabled) {
        transform: translateY(-2px);
      }

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
      }
    }
  }
}

@media (max-width: 768px) {
  .contact-form-card {
    padding: 2rem 1.5rem;

    .contact-form {
      .form-row {
        flex-direction: column;
        gap: 0;
      }

      .form-group {
        .form-label {
          font-size: 1rem;
        }

        .input-wrapper .form-control,
        .select-wrapper .form-control {
          font-size: 1rem;
          padding: 14px 18px;
        }
      }

      .btn-submit-form {
        padding: 18px 35px;
        font-size: 1.1rem;
      }
    }
  }
}
</style>
